// getIsMatchedPathname.js
'use strict';
import { matchPath } from 'react-router';
import { freezoneType } from '../resource/freezoneConstants.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';
import { WHITE_LIST_PATHS_REGEX } from '../resource/nextjsWhiteListPaths.js';
import { PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG } from '../resource/langPath.js';

/**
 * Helper to create path patterns with optional language prefix support
 * @param {string|string[]} paths - single path or array of paths
 * @returns {object} object with paths array including lang variants
 */
const withLangSupport = paths => {
  const pathArray = Array.isArray(paths) ? paths : [paths];
  return {
    paths: pathArray.flatMap(path => [
      { end: true, path: path },
      ...PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.map(lang => ({
        end: true,
        path: `/${lang}${path}`,
      })),
    ]),
  };
};

const matchOptions = {
  isOnNext: {
    paths: WHITE_LIST_PATHS_REGEX.map(path => ({
      path,
      ends: true,
    })),
  },
  isOnFreezone: {
    amatuer: withLangSupport(`/freezone/${freezoneType.AMATEUR}`),
    model: withLangSupport(`/freezone/${freezoneType.MODEL}`),
  },
  isOnFreezoneDetail: withLangSupport('/freezone/:id'),
  isOnFlixDetail: withLangSupport('/flix/:id'),
  isOnJoin: withLangSupport('/join/:progress'),
  isOnPostDetail: withLangSupport([
    '/post/:id',
    '/chat/:chatroomId/message/:messageId',
  ]),
  isOnChatUser: withLangSupport('/chat/user/:id'),
  isOnProfile: withLangSupport([
    '/u/:username',
    '/u/:username/:listType',
    '/user/:userId',
    '/user/:userId/:listType',
  ]),
  isOnLivestreamDetail: withLangSupport([
    `/u/:username/${LIVESTREAM_PATH}`,
    `/user/:userId/${LIVESTREAM_PATH}`,
  ]),
  isOnLivestreamChat: withLangSupport([
    `/u/:username/${LIVESTREAM_PATH}/chat`,
    `/user/:userId/${LIVESTREAM_PATH}/chat`,
  ]),
  isOnFaq: withLangSupport('/settings/faq'),
  isOnTos: withLangSupport('/tos'),
  isOnPrivacy: withLangSupport('/privacy'),
  isOnDebug: withLangSupport('/settings/more/debug'),
  isOnAbout: withLangSupport('/settings/more/about'),
  isOn2257: withLangSupport('/settings/2257'),
  isOnAddvalue: withLangSupport('/addvalue'),
  isOnRevenue: withLangSupport(['/revenue', '/revenue/report/:reportType']),
  isOnManageProfileAccount: withLangSupport('/manage-profile/account'),
};

/**
 * Get is matched pathname
 * @param {string} pathname - current pathname.
 * @param {string} dataKey - data key.
 * @param {object} {option} - `matchPath` options.
 * @returns {boolean} return true if current pathname is on certain pathname.
 */
const getIsMatchedPathname = ({ pathname, dataKey, option = {} } = {}) => {
  if (!pathname || !dataKey || !matchOptions[dataKey]) {
    return false;
  }
  switch (dataKey) {
    case 'isOnFreezoneDetail': {
      const isOnFreezone = getIsOnFreezone({ pathname }) !== null;
      const isOnFreezoneDetail = matchOptions[dataKey].paths.some(
        pattern => matchPath(pattern, pathname) != null
      );
      return !isOnFreezone && isOnFreezoneDetail;
    }
    case 'isOnFreezone': {
      return getIsOnFreezone({ pathname }) !== null;
    }
    case 'isOnProfile': {
      const profilePatterns = matchOptions[dataKey]?.paths || [];
      const isOnLivestream = [
        ...(matchOptions['isOnLivestreamDetail']?.paths || []),
        ...(matchOptions['isOnLivestreamChat']?.paths || []),
      ].some(pattern => matchPath(pattern, pathname) != null);
      const isOnProfile = profilePatterns.some(
        pattern => matchPath(pattern, pathname) != null
      );
      return isOnProfile && !isOnLivestream;
    }
    case 'isOnNext': {
      return matchOptions[dataKey].paths.some(
        pattern => matchPath({ ...pattern, ...option }, pathname) != null
      );
    }
    default: {
      if (!matchOptions[dataKey]) return false;
      const patterns = [];
      const paths = matchOptions[dataKey].paths;
      if (paths) {
        patterns.push(...paths);
      } else {
        patterns.push(matchOptions[dataKey]);
      }
      return patterns.some(pattern => matchPath(pattern, pathname) != null);
    }
  }
};

export default getIsMatchedPathname;

const getIsOnFreezone = ({ pathname }) => {
  if (!pathname) return null;

  const amateuer = matchOptions.isOnFreezone.amatuer.paths.find(
    pattern => matchPath(pattern, pathname) != null
  );
  const model = matchOptions.isOnFreezone.model.paths.find(
    pattern => matchPath(pattern, pathname) != null
  );
  return amateuer || model || null;
};
